import { useCallback } from "react";
import {
  doc,
  getDoc,
  serverTimestamp,
  deleteField,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../config/firebase";

const TRANSFER_BATCH_CHUNK = 400;

/**
 * Admin/parentsUser: move assignments.first[sourcePn] → assignments.first[targetPn] per inventory doc.
 * Reads latest doc state via getDoc for safe merge on bulk lines.
 * @returns {Promise<number>} number of inventory docs updated in this batch
 */
export async function addTransferFirstAssignmentsToBatch(
  batch,
  { sourcePersonalNumber, targetEmployee, currentUser, rows },
) {
  const targetPn = targetEmployee.personalNumber;
  const assignedBy = currentUser.employeeId || currentUser.email || "";
  let updated = 0;

  for (const row of rows) {
    const ref = doc(db, "inventory", row.id);
    const snap = await getDoc(ref);
    const data = snap.data();
    if (!data) continue;

    const src = data.assignments?.first?.[sourcePersonalNumber];
    if (!src) continue;

    const isSerial =
      data.serialNumber && String(data.serialNumber).trim() !== "";

    if (isSerial) {
      const targetExisting = data.assignments?.first?.[targetPn];
      if (targetExisting && targetPn !== sourcePersonalNumber) {
        throw new Error(
          `פריט סידורי ${data.serialNumber || row.id}: כבר משויך ליעד; לא ניתן להעביר`,
        );
      }
      if (targetPn === sourcePersonalNumber) continue;

      batch.update(ref, {
        [`assignments.first.${sourcePersonalNumber}`]: deleteField(),
        [`assignments.first.${targetPn}`]: {
          quantity: src.quantity ?? 1,
          assignedAt: serverTimestamp(),
          firstName: targetEmployee.firstName || "",
          lastName: targetEmployee.lastName || "",
          assignedBy,
          note: src.note || "",
        },
        status: "assigned",
        updatedAt: serverTimestamp(),
      });
    } else {
      const qtyFrom = src.quantity || 0;
      if (qtyFrom <= 0) continue;

      const existingTargetEntry = data.assignments?.first?.[targetPn];
      const existingTargetQty = existingTargetEntry?.quantity || 0;
      const newQty = qtyFrom + existingTargetQty;
      const mergedNote =
        existingTargetQty > 0
          ? existingTargetEntry?.note || src.note || ""
          : src.note || "";

      batch.update(ref, {
        [`assignments.first.${sourcePersonalNumber}`]: deleteField(),
        [`assignments.first.${targetPn}`]: {
          quantity: newQty,
          assignedAt: serverTimestamp(),
          firstName: targetEmployee.firstName || "",
          lastName: targetEmployee.lastName || "",
          assignedBy,
          note: mergedNote || "",
        },
        updatedAt: serverTimestamp(),
      });
    }
    updated++;
  }

  return updated;
}

/**
 * Commit transfer in chunks (Firestore 500-op limit). Inventory updates only — create audit `assignments` doc in caller via addDoc.
 */
export async function commitInventoryFirstTransferInChunks({
  rows,
  sourcePersonalNumber,
  targetEmployee,
  currentUser,
}) {
  const chunks = [];
  for (let i = 0; i < rows.length; i += TRANSFER_BATCH_CHUNK) {
    chunks.push(rows.slice(i, i + TRANSFER_BATCH_CHUNK));
  }

  if (chunks.length === 0) {
    return { totalUpdated: 0 };
  }

  let totalUpdated = 0;

  for (let c = 0; c < chunks.length; c++) {
    const batch = writeBatch(db);
    const n = await addTransferFirstAssignmentsToBatch(batch, {
      sourcePersonalNumber,
      targetEmployee,
      currentUser,
      rows: chunks[c],
    });
    totalUpdated += n;
    if (n > 0) {
      await batch.commit();
    }
  }

  return { totalUpdated };
}

/**
 * Firestore batch helpers for assigning inventory to / returning from personnel.
 */
export const useSignatureInventoryBatchOps = ({
  inventory,
  currentUser,
  isChildUser,
}) => {
  const handleAssignItem = useCallback(
    async (batch, cartItem, selectedEmployee, quantityToAssign) => {
      let inventoryItemId = cartItem.id;

      if (!cartItem.hasSerialNumber) {
        if (isChildUser && currentUser.employeeId) {
          const assignedItem = inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === cartItem.itemDefinitionId &&
              invItem.assignments?.first?.[currentUser.employeeId] &&
              (cartItem.isOrganic === null ||
                Boolean(invItem.isOrganic) === Boolean(cartItem.isOrganic)),
          );

          if (!assignedItem) {
            console.error(
              "No assigned inventory item found for childUser assignment",
            );
            return;
          }

          inventoryItemId = assignedItem.id;
        } else {
          const pn = selectedEmployee.personalNumber;
          const pnKey = pn != null ? String(pn).trim() : "";
          let useCartRow =
            Boolean(cartItem.id) &&
            pnKey &&
            (() => {
              const row = inventory.find((invItem) => invItem.id === cartItem.id);
              return (
                row &&
                row.itemDefinitionId === cartItem.itemDefinitionId &&
                (cartItem.isOrganic === null ||
                  Boolean(row.isOrganic) === Boolean(cartItem.isOrganic)) &&
                (row.assignments?.first?.[pnKey]?.quantity ?? 0) > 0
              );
            })();

          if (useCartRow) {
            inventoryItemId = cartItem.id;
          } else {
            const availableItem = inventory.find(
              (invItem) =>
                invItem.itemDefinitionId === cartItem.itemDefinitionId &&
                (invItem.availableQuantity ?? 0) > 0 &&
                (cartItem.isOrganic === null ||
                  Boolean(invItem.isOrganic) === Boolean(cartItem.isOrganic)),
            );

            if (!availableItem) {
              console.error("No available inventory item found for assignment");
              return;
            }

            inventoryItemId = availableItem.id;
          }
        }
      }

      const inventoryItemRef = doc(db, "inventory", inventoryItemId);

      const currentDoc = await getDoc(inventoryItemRef);
      const currentItemData = currentDoc.data();

      const originalQuantity = cartItem.originalQuantity || 0;
      const finalQuantity = originalQuantity + quantityToAssign;

      const currentItem =
        inventory.find((item) => item.id === inventoryItemId) ||
        currentItemData;

      const updateData = {
        updatedAt: serverTimestamp(),
      };

      if (isChildUser && currentUser.employeeId) {
        updateData[`assignments.second.${selectedEmployee.personalNumber}`] = {
          quantity: finalQuantity,
          assignedAt: serverTimestamp(),
          firstName: selectedEmployee.firstName || "",
          lastName: selectedEmployee.lastName || "",
          assignedBy: currentUser.employeeId,
          note: cartItem.note || "",
        };
      } else {
        updateData[`assignments.first.${selectedEmployee.personalNumber}`] = {
          quantity: finalQuantity,
          assignedAt: serverTimestamp(),
          firstName: selectedEmployee.firstName || "",
          lastName: selectedEmployee.lastName || "",
          assignedBy: currentUser.employeeId || currentUser.email || "",
          note: cartItem.note || "",
        };
      }

      if (!cartItem.hasSerialNumber && currentItem) {
        if (isChildUser) {
          // For childUser: availableQuantity calculated by useInventoryCalculations
        } else {
          const currentAvailableQuantity =
            currentItem.availableQuantity ?? currentItem.quantity ?? 0;
          const newAvailableQuantity = Math.max(
            0,
            currentAvailableQuantity - quantityToAssign,
          );

          updateData.availableQuantity = newAvailableQuantity;
          updateData.status =
            newAvailableQuantity === 0 ? "assigned" : "available";
        }
      } else if (!isChildUser) {
        updateData.status = "assigned";
      }

      batch.update(inventoryItemRef, updateData);
    },
    [inventory, currentUser, isChildUser],
  );

  const handleReturnItem = useCallback(
    async (batch, cartItem, selectedEmployee, quantityToReturn) => {
      let inventoryItemId = cartItem.id;

      if (!cartItem.hasSerialNumber) {
        let assignedItem;

        if (isChildUser && currentUser.employeeId) {
          assignedItem = inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === cartItem.itemDefinitionId &&
              invItem.assignments?.first?.[currentUser.employeeId] &&
              invItem.assignments?.second?.[selectedEmployee.personalNumber] &&
              (cartItem.isOrganic === null ||
                Boolean(invItem.isOrganic) === Boolean(cartItem.isOrganic)),
          );
        } else {
          assignedItem = inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === cartItem.itemDefinitionId &&
              invItem.assignments?.first?.[selectedEmployee.personalNumber] &&
              (cartItem.isOrganic === null ||
                Boolean(invItem.isOrganic) === Boolean(cartItem.isOrganic)),
          );
        }

        if (!assignedItem) {
          console.error("No assigned inventory item found for return");
          alert("שגיאה: לא נמצא פריט מוחתם להחזרה. נא לנסות שוב.");
          return;
        }

        inventoryItemId = assignedItem.id;

        const inventoryItemRef = doc(db, "inventory", inventoryItemId);
        const currentDoc = await getDoc(inventoryItemRef);
        const currentItemData = currentDoc.data();

        if (isChildUser && currentUser.employeeId) {
          const currentAssignedQuantity =
            currentItemData?.assignments?.second?.[
              selectedEmployee.personalNumber
            ]?.quantity ||
            assignedItem.assignments?.second?.[selectedEmployee.personalNumber]
              ?.quantity ||
            0;
          const newAssignedQuantity =
            currentAssignedQuantity - quantityToReturn;

          if (newAssignedQuantity <= 0) {
            batch.update(inventoryItemRef, {
              [`assignments.second.${selectedEmployee.personalNumber}`]:
                deleteField(),
              updatedAt: serverTimestamp(),
            });
          } else {
            batch.update(inventoryItemRef, {
              [`assignments.second.${selectedEmployee.personalNumber}.quantity`]:
                newAssignedQuantity,
              [`assignments.second.${selectedEmployee.personalNumber}.assignedAt`]:
                serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          }
        } else {
          const currentAssignedQuantity =
            currentItemData?.assignments?.first?.[
              selectedEmployee.personalNumber
            ]?.quantity ||
            assignedItem.assignments?.first?.[selectedEmployee.personalNumber]
              ?.quantity ||
            0;
          const newAssignedQuantity =
            currentAssignedQuantity - quantityToReturn;

          const currentAvailableQuantity =
            currentItemData?.availableQuantity ??
            assignedItem.availableQuantity ??
            assignedItem.quantity ??
            0;
          const newAvailableQuantity =
            currentAvailableQuantity + quantityToReturn;

          if (newAssignedQuantity <= 0) {
            batch.update(inventoryItemRef, {
              [`assignments.first.${selectedEmployee.personalNumber}`]:
                deleteField(),
              availableQuantity: newAvailableQuantity,
              status: "available",
              updatedAt: serverTimestamp(),
            });
          } else {
            batch.update(inventoryItemRef, {
              [`assignments.first.${selectedEmployee.personalNumber}.quantity`]:
                newAssignedQuantity,
              [`assignments.first.${selectedEmployee.personalNumber}.assignedAt`]:
                serverTimestamp(),
              availableQuantity: newAvailableQuantity,
              status: "available",
              updatedAt: serverTimestamp(),
            });
          }
        }
      } else if (isChildUser && currentUser.employeeId) {
        const assignedItem = inventory.find(
          (invItem) =>
            invItem.id === inventoryItemId &&
            invItem.assignments?.first?.[currentUser.employeeId] &&
            invItem.assignments?.second?.[selectedEmployee.personalNumber],
        );

        if (assignedItem) {
          const inventoryItemRef = doc(db, "inventory", inventoryItemId);
          batch.update(inventoryItemRef, {
            [`assignments.second.${selectedEmployee.personalNumber}`]:
              deleteField(),
            updatedAt: serverTimestamp(),
          });
        } else {
          console.error("No assigned inventory item found for return");
          alert("שגיאה: לא נמצא פריט מוחתם להחזרה. נא לנסות שוב.");
        }
      } else {
        const inventoryItemRef = doc(db, "inventory", inventoryItemId);
        batch.update(inventoryItemRef, {
          [`assignments.first.${selectedEmployee.personalNumber}`]:
            deleteField(),
          status: "available",
          updatedAt: serverTimestamp(),
        });
      }
    },
    [inventory, isChildUser, currentUser],
  );

  return { handleAssignItem, handleReturnItem };
};
